import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core'
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'

@Component({
  selector: 'basin-textbox-with-units',
  templateUrl: './textbox-with-units.component.html',
  styleUrls: [],
  providers: [
    // This registers the component as a NG_VALUE_ACCESSOR provider for form control.
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextboxWithUnitsComponent),
      multi: true,
    },
  ],
})
export class TextboxWithUnitsComponent implements ControlValueAccessor {
  @Input() prepend: string = ''
  @Input() append: string = ''
  @Input() min: number
  @Input() max: number
  @Input() numberType: boolean = true
  @Input() styleClass: string
  @Input() id: string
  @Input() name: string
  @Input() placeholder: string = ''
  @Input() minlength: number
  @Input() maxlength: number
  @Input() readonly: boolean
  @Input() required: boolean
  @Input() disabled: boolean
  @Input() formControl: FormControl
  @Input() ngModel: string | number
  @Input() updateOnBlurOnly: boolean = true
  @Input() defaultValue: number
  @Output() ngModelChange: EventEmitter<string | number> = new EventEmitter<
    string | number
  >()
  @Output() blur: EventEmitter<string | number> = new EventEmitter<
    string | number
  >()
  onModelChange: Function = () => {}
  onModelTouched: Function = () => {}
  displayValue: string | number
  tempValue: string | number

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnChanges() {
    if (this.ngModel) {
      this.displayValue = this.prepend + String(this.ngModel) + this.append
      this.tempValue = this.ngModel
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    this.cdRef.markForCheck()
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      if (
        this.numberType &&
        (value === '' || value === null || value === undefined)
      ) {
        this.ngModel = ''
      } else {
        this.ngModel =
          this.numberType && !isNaN(Number(value)) ? Number(value) : value
      }
      this.displayValue = this.prepend + String(this.ngModel) + this.append
      if (!this.ngModel && this.ngModel !== 0) {
        this.displayValue = ''
      }
      this.tempValue = this.ngModel
      this.cdRef.markForCheck()
    }
  }

  displayValueChange(value: string) {
    let numValue = Number(value)
    // Ensure the value stays within specified min and max bounds.
    if (!isNaN(numValue) && value !== '') {
      if (numValue > this.max) {
        numValue = this.max
      } else if (numValue < this.min) {
        numValue = this.min
      }
      this.tempValue = numValue
    } else {
      this.tempValue = value
    }
    // Emit model change immediately if not restricted to onBlur.
    if (!this.updateOnBlurOnly) {
      this.ngModelChange.emit(this.tempValue)
      this.onModelChange(this.tempValue)
    }
  }

  onFocus() {
    this.displayValue = this.ngModel
  }

  onBlur() {
    if (
      this.numberType &&
      (this.tempValue === '' ||
        this.tempValue === null ||
        this.tempValue === undefined)
    ) {
      if (this.defaultValue) {
        this.ngModel = this.defaultValue
      } else {
        this.ngModel = ''
      }
    } else {
      this.ngModel =
        this.numberType && !isNaN(Number(this.tempValue))
          ? Number(this.tempValue)
          : this.tempValue
    }
    this.displayValue = this.prepend + String(this.ngModel) + this.append
    if (!this.ngModel && this.ngModel !== 0) {
      this.displayValue = ''
    }
    if (this.updateOnBlurOnly) {
      this.ngModelChange.emit(this.ngModel)
      this.onModelChange(this.ngModel)
    }
    this.onModelTouched()
    this.blur.emit(this.ngModel)
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn
  }
}
